import React, { useEffect } from 'react';
import {
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { wsConnect, wsDisconnect } from '../../app/store/websocketSlice';

// True immediately after an OAuth/SAML redirect back from Cognito, before Amplify finishes the token
// exchange and rewrites the URL - `user`/`route` won't reflect the new session yet during that window.
const isOAuthCallbackInProgress = (search: string) => {
  const params = new URLSearchParams(search);
  return params.has('code') || params.has('error');
};

export default function ProtectedRoute() {
  const { user } = useAppSelector(state => state.user);
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const location = useLocation();

  const { route } = useAuthenticator((context) => [context.route]);

  const isAuthenticated = !!user && (route === 'authenticated' || route === 'idle');

  useEffect(() => {
    if (isAuthenticated) return;
    if (isOAuthCallbackInProgress(location.search)) return;

    const redirectTo = `${location.pathname}${location.search}`;

    navigate(`/?redirect=${encodeURIComponent(redirectTo)}`, {
      replace: true,
    });
  }, [user, route, location.pathname, location.search, navigate, isAuthenticated]);

  // Open the realtime WebSocket once authenticated; close it on logout/leaving the app.
  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    dispatch(wsConnect());
    return () => {
      dispatch(wsDisconnect());
    };
  }, [isAuthenticated, dispatch]);

  // Don't render protected content (or run its mount-time effects/data fetches) until we've
  // actually confirmed the user is authenticated - previously this rendered unconditionally and
  // only used the check to decide whether to redirect, so children mounted (and could fetch data,
  // or force a logout of their own) before auth had a chance to resolve, e.g. immediately after an
  // OAuth/SAML redirect back from Cognito, before the token exchange and user fetch complete.
  if (!isAuthenticated) return null;

  return (
    <Outlet />
  );
}
